import { Stack, Typography, Box } from '@mui/material';
import { StyledIconButton } from "../../../shared/Components/StyledIconButton";
import { FlightingItemDisplay } from './FlightingItemDisplay';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { ArrowDownwardSharp } from '@mui/icons-material';
import { isLastFlightInDivision } from './flightingMutations';


interface FlightBlockProps {
    divisionIndex: number;
    viewEvent: any;
    editMode: boolean;
    appTheme: any;
    factor: number;
    transitioning: boolean;
    /** When provided, use this list instead of viewEvent.flighting.flights (e.g. effective flights when divisions-only) */
    flightsToShow?: any[];
    /** When true, hide add/delete flight actions (divisions-as-flights mode) */
    isDivisionsOnly?: boolean;
    onAddFlight: (divisionIndex: number, templateFlight: any) => void;
    onDeleteFlight: (flightIndex: number) => void;
}



export const FlightBlock = ({
    divisionIndex,
    viewEvent,
    editMode,
    appTheme,
    factor,
    transitioning,
    flightsToShow,
    isDivisionsOnly,
    onAddFlight,
    onDeleteFlight,
}: FlightBlockProps) => {
    const realFlights = viewEvent?.flighting?.flights ?? [];
    const flights = flightsToShow ?? realFlights;
    const showAddDelete = !isDivisionsOnly;

    return (
        <Stack direction='column' spacing={1} flex={1} padding={'0px 20px'}
        >
            <Stack direction='row' spacing={3} flex={1} alignItems='center'>
                    {viewEvent?.flighting?.props?.flightingFields?.filter((field: any) => field.level === 'flight')
                        .map((field: any) => (
                            <Typography key={field.field} variant='subtitle2' color='primary' 
                                sx={{ fontWeight: 500, flex: 1, textIndent: field.field === 'flightName' ? '5px' : '0px',
                                    textAlign: field.field === 'flightName' ? 'left' : 'center',
                                    minWidth: field.field === 'flightName' ? '120px' : 'max-content',
                                    transition: 'all 0.3s ease-in-out',
                                    opacity: transitioning && field.field !== 'flightName' ? 0.5 : 1,
                                 }}
                            >
                                {field.label}
                            </Typography>
                    ))}

                    <Typography variant='subtitle2' color='primary' 
                        sx={{ fontWeight: 500, width: 80*factor, textAlign: 'center',
                            transition: 'all 0.3s ease-in-out',
                            opacity: transitioning ? 0.5 : 1,
                         }}>Actions</Typography>
                </Stack>
            {flights.filter((flight: any) => flight.divisionIndex === divisionIndex)
                .map((flight: any) => (
                <Stack key={flight.index} direction='row' spacing={3} flex={1} alignItems='center'>

                    {Object.keys(flight).sort((a, b) => {
                        if (a === 'flightName') return -1;
                        if (b === 'flightName') return 1;

                        const aIndex = viewEvent.flighting.props.flightingFields.findIndex((f: any) => f.field === a);
                        const bIndex = viewEvent.flighting.props.flightingFields.findIndex((f: any) => f.field === b);

                        if (aIndex !== -1 && bIndex !== -1) {
                            return aIndex - bIndex;
                        }

                        return a.localeCompare(b);
                    }).map((key: string) => (
                        (!['index', 'players', 'divisionOnly'].includes(key) 
                            && !key.includes('division') &&
                            <Box key={key} flex={1} minWidth={key === 'flightName' ? '120px' : '0px'}>
                                <FlightingItemDisplay
                                    index={flight.index}
                                    orient='row'
                                    label={key.replace('flight', '')}
                                    keyName={key}
                                    level='flight'
                                    hideLabel={true}
                                    align={key === 'flightName' ? 'left' : 'center'}
                                />
                            </Box>)
                    ))}

                    {editMode && showAddDelete ? (
                        <Stack direction='row' sx={{ 
                            minWidth: 80*factor, 
                            height: 25*factor, 
                            alignContent: 'center', 
                            justifyContent: 'center',
                        }}>
                            {isLastFlightInDivision(realFlights, divisionIndex, flight.index) ? (
                                <StyledIconButton
                                    icon={<><AddIcon sx={{ fontSize: 14*factor }} /> <ArrowDownwardSharp sx={{ fontSize: 12*factor }} /></>}
                                    label=''
                                    appTheme={appTheme}
                                    factor={factor*0.8}
                                    color='primary'
                                    width={38*factor}
                                    height={'max-content'}
                                    disabled={!editMode}
                                    onClick={() => onAddFlight(divisionIndex, flight)}
                                />
                            ) : (
                                <Box sx={{ minWidth: 38*factor, height: 25*factor }}></Box>
                            )}
                            <StyledIconButton
                                appTheme={appTheme}
                                factor={factor}
                                label=''
                                color='error'
                                icon={<DeleteIcon sx={{ fontSize: 16*factor }} />}
                                disabled={!editMode}
                                width={30*factor}
                                height={25*factor}
                                onClick={() => onDeleteFlight(flight.index)}
                            /> 
                        </Stack>
                    ) : (
                        <Box sx={{ minWidth: 80*factor, height: 25*factor }}></Box>
                    )}
                </Stack>
            ))}
        </Stack>                                      
    )
}
